import { getFormProps, useForm } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod/v4'
import { format } from 'date-fns'
import { useState } from 'react'
import { data, Link, useLoaderData, useFetcher, Form } from 'react-router'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { FeedbackForm } from '#app/components/feedback/feedback-form.tsx'
import {
	Alert,
	AlertDescription,
	AlertTitle,
} from '#app/components/ui/alert.tsx'
import { Badge } from '#app/components/ui/badge.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { Card, CardContent } from '#app/components/ui/card.tsx'
import { CopyButton } from '#app/components/ui/copy-button.tsx'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from '#app/components/ui/dialog.tsx'
import { Icon } from '#app/components/ui/icon.js'
import { Separator } from '#app/components/ui/separator.tsx'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '#app/components/ui/tooltip.tsx'
import { useCanonical } from '#app/seo/hooks.ts'
import { type SeoProps } from '#app/seo/seo.tsx'
import { formatPrice } from '#app/utils/currency.ts'
import { prisma } from '#app/utils/db.server.ts'
import { useOptionalUser } from '#app/utils/user.ts'
import { type Route } from './+types/$slug.ts'
import { submitRentalInquiry } from './__inquiry.server.tsx'
import { InquiryFormFields } from './components/inquiry-form-fields.tsx'
import { RentalInquirySubmitSchema } from './inquiry-schema.ts'

export async function loader({ params }: Route.LoaderArgs) {
	const { slug } = params

	const rental = await prisma.rentalListing.findUnique({
		where: { slug, status: 'published' },
		select: {
			id: true,
			title: true,
			slug: true,
			description: true,
			dailyRateCents: true,
			weeklyRateCents: true,
			monthlyRateCents: true,
			hourlyRateCents: true,
			depositCents: true,
			quantityAvailable: true,
			contactEmail: true,
			contactPhone: true,
			availableFrom: true,
			availableUntil: true,
			createdAt: true,
			// New optional fields
			requiresDeposit: true,
			cleaningFeeCents: true,
			setupFeeCents: true,
			minRentalAmount: true,
			minRentalUnit: true,
			maxRentalAmount: true,
			maxRentalUnit: true,
			leadTimeHours: true,
			availabilityType: true,
			deliveryAvailable: true,
			deliveryRadiusMiles: true,
			deliveryFeeCents: true,
			deliveryFeeIsStartingAt: true,
			pickupInstructions: true,
			preferredContactMethod: true,
			displayPhone: true,
			displayEmail: true,
			requirementsText: true,
			allowedRules: true,
			notAllowedRules: true,
			brand: true,
			model: true,
			year: true,
			condition: true,
			specs: true,
			replacementValueCents: true,
			setupIncluded: true,
			setupDescription: true,
			operatorAvailable: true,
			operatorFeeCents: true,
			operatorDescription: true,
			storageRequirements: true,
			cancellationPolicy: true,
			lateReturnFeeCentsPerHour: true,
			damagePolicy: true,
			featuredMedia: {
				select: {
					id: true,
					name: true,
					title: true,
					altText: true,
					url: true,
				},
			},
			images: {
				select: {
					id: true,
					sortOrder: true,
					altText: true,
					media: {
						select: {
							id: true,
							url: true,
							altText: true,
						},
					},
				},
				orderBy: {
					sortOrder: 'asc',
				},
			},
			categories: {
				select: {
					id: true,
					name: true,
					slug: true,
				},
			},
			subcategories: {
				select: {
					id: true,
					name: true,
					slug: true,
				},
			},
			cities: {
				select: {
					id: true,
					name: true,
					slug: true,
				},
			},
			ownerUser: {
				select: {
					id: true,
					name: true,
				},
			},
			ownerBusiness: {
				select: {
					id: true,
					name: true,
					slug: true,
				},
			},
			seoMeta: {
				select: {
					title: true,
					description: true,
					canonicalUrl: true,
					focusKeyword: true,
					seoKeywords: true,
					noIndex: true,
					ogTitle: true,
					ogDescription: true,
					ogImage: true,
					ogType: true,
					twitterTitle: true,
					twitterDescription: true,
					twitterImage: true,
					twitterCard: true,
					structuredData: true,
				},
			},
		},
	})

	if (!rental) {
		throw new Response('Rental not found', { status: 404 })
	}

	return data({ rental })
}

export async function action({ request }: Route.ActionArgs) {
	return submitRentalInquiry(request)
}

// Breadcrumb handle for automatic breadcrumb generation
export const handle = {
	breadcrumb({ data }: { data: Awaited<ReturnType<typeof loader>>['data'] }) {
		const rental = data?.rental
		return rental
			? { name: rental.title, url: `/rentals/${rental.slug}` }
			: null
	},
	seo({
		loaderData,
		origin,
	}: {
		loaderData: Awaited<ReturnType<typeof loader>>['data']
		origin: string
		params: Record<string, string>
		pathname: string
	}): SeoProps | null {
		const rental = loaderData?.rental
		if (!rental) return null

		const seo = rental.seoMeta

		// Optimization: Truncate city list for meta tags to avoid overly long strings
		const cityCount = rental.cities.length
		const displayCities = rental.cities.slice(0, 3).map((c) => c.name)
		let cityNames = displayCities.join(', ')
		if (cityCount > 3) {
			cityNames += ` and ${cityCount - 3} more`
		}

		const canonicalUrl = seo?.canonicalUrl || `${origin}/rentals/${rental.slug}`
		const defaultDescription =
			rental.description ||
			`Rent ${rental.title} in ${cityNames}. Contact us for availability and pricing.`

		// Build keywords from focus keyword, SEO keywords, and categories
		const keywords = [
			seo?.focusKeyword,
			...(seo?.seoKeywords || []),
			...rental.categories.map((c) => c.name),
		].filter(Boolean)

		// Build structured data
		const structuredData: Array<object | string> = []

		// Append custom structured data from DB if present
		if (seo?.structuredData) {
			const customData = seo.structuredData
			if (Array.isArray(customData)) {
				structuredData.push(...(customData as Array<object | string>))
			} else if (customData !== null) {
				structuredData.push(customData as object | string)
			}
		}

		return {
			title: seo?.title || `${rental.title} - ${cityNames} - RentDFW.com`,
			description: seo?.description || defaultDescription,
			canonical: canonicalUrl,
			keywords: keywords.length > 0 ? keywords : undefined,
			robots: seo?.noIndex ? 'noindex, nofollow' : undefined,
			openGraph: {
				title: seo?.ogTitle || rental.title,
				description: seo?.ogDescription || defaultDescription,
				...(seo?.ogImage || rental.featuredMedia?.url
					? {
							image: seo?.ogImage || rental.featuredMedia?.url || undefined,
						}
					: {}),
				type: seo?.ogType || 'website',
				url: canonicalUrl,
			},
			twitter: {
				card:
					(seo?.twitterCard as 'summary_large_image' | 'summary') ||
					'summary_large_image',
				title: seo?.twitterTitle || rental.title,
				description: seo?.twitterDescription || defaultDescription,
				...(seo?.twitterImage || rental.featuredMedia?.url
					? {
							image:
								seo?.twitterImage || rental.featuredMedia?.url || undefined,
						}
					: {}),
			},
			structuredData: structuredData.length > 0 ? structuredData : undefined,
		}
	},
}

export default function RentalDetailPage() {
	const { rental } = useLoaderData<typeof loader>()
	const canonicalUrl = useCanonical(`/rentals/${rental.slug}`)
	const [flagDialogOpen, setFlagDialogOpen] = useState(false)
	const [bookingDialogOpen, setBookingDialogOpen] = useState(false)
	const [lightboxOpen, setLightboxOpen] = useState(false)
	const [currentImageIndex, setCurrentImageIndex] = useState(0)
	const user = useOptionalUser()

	// Split user name into first and last if available
	const nameParts = user?.name?.trim().split(/\s+/) || []
	const firstName = nameParts[0] || ''
	const lastName = nameParts.slice(1).join(' ') || ''

	// Inline page form state
	const [pageForm, pageFields] = useForm({
		id: 'rental-inquiry-form',
		constraint: getZodConstraint(RentalInquirySubmitSchema),
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: RentalInquirySubmitSchema })
		},
		defaultValue: {
			firstName,
			lastName,
			email: user?.email || '',
			phone: '',
			message: '',
		},
		shouldRevalidate: 'onBlur',
	})

	// Dialog form state + fetcher (separate id, wire server errors)
	const inquiryFetcher = useFetcher()
	const [dialogForm, dialogFields] = useForm({
		id: 'rental-inquiry-dialog-form',
		constraint: getZodConstraint(RentalInquirySubmitSchema),
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: RentalInquirySubmitSchema })
		},
		defaultValue: {
			firstName,
			lastName,
			email: user?.email || '',
			phone: '',
			message: '',
		},
		shouldRevalidate: 'onBlur',
		lastResult: inquiryFetcher.data,
	})

	// Optional: close the dialog after a successful submit (like @feedback)
	const dialogSuccess =
		inquiryFetcher.data &&
		typeof inquiryFetcher.data === 'object' &&
		'success' in inquiryFetcher.data
			? Boolean(inquiryFetcher.data.success)
			: false
	if (dialogSuccess) setTimeout(() => setBookingDialogOpen(false), 1500)

	const isDialogSubmitting = inquiryFetcher.state !== 'idle'

	// Label mappings
	const CONDITION_LABELS: Record<'new' | 'like_new' | 'good' | 'worn', string> =
		{
			new: 'New',
			like_new: 'Like new',
			good: 'Good',
			worn: 'Worn',
		}

	const availFromStr = rental.availableFrom
		? format(new Date(rental.availableFrom), 'MMM d, yyyy')
		: 'Now'
	const availUntilStr = rental.availableUntil
		? format(new Date(rental.availableUntil), 'MMM d, yyyy')
		: 'Indefinite'

	// Combine featured media and gallery images
	const allImages = [
		rental.featuredMedia,
		...rental.images.map((img) => img.media),
	].filter((media): media is NonNullable<typeof rental.featuredMedia> =>
		Boolean(media),
	)

	// Determine pricing display - Prioritize rates for header
	const primaryRate =
		rental.hourlyRateCents ??
		rental.dailyRateCents ??
		rental.weeklyRateCents ??
		rental.monthlyRateCents

	const primaryRateLabel =
		rental.hourlyRateCents != null
			? '/hour'
			: rental.dailyRateCents != null
				? '/day'
				: rental.weeklyRateCents != null
					? '/week'
					: rental.monthlyRateCents != null
						? '/month'
						: undefined

	const pricingOptions = [
		rental.hourlyRateCents && {
			label: 'Hourly',
			value: formatPrice(rental.hourlyRateCents),
		},
		rental.dailyRateCents && {
			label: 'Daily',
			value: formatPrice(rental.dailyRateCents),
		},
		rental.weeklyRateCents && {
			label: 'Weekly',
			value: formatPrice(rental.weeklyRateCents),
		},
		rental.monthlyRateCents && {
			label: 'Monthly',
			value: formatPrice(rental.monthlyRateCents),
		},
	].filter(Boolean)

	const handleImageClick = (index: number) => {
		setCurrentImageIndex(index)
		setLightboxOpen(true)
	}

	const handlePreviousImage = () => {
		setCurrentImageIndex((prev) =>
			prev === 0 ? allImages.length - 1 : prev - 1,
		)
	}

	const handleNextImage = () => {
		setCurrentImageIndex((prev) =>
			prev === allImages.length - 1 ? 0 : prev + 1,
		)
	}

	return (
		<>
			{/* Image Lightbox Dialog */}
			<Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
				<DialogContent className="max-w-5xl gap-0 p-0">
					<DialogHeader>
						<DialogTitle className="sr-only">Image Gallery</DialogTitle>
					</DialogHeader>
					<div className="relative">
						{/* Image */}
						{allImages[currentImageIndex] && (
							<div className="relative">
								<img
									src={allImages[currentImageIndex].url || ''}
									alt={allImages[currentImageIndex].altText || rental.title}
									className="h-auto w-full rounded-t-lg object-contain"
								/>
							</div>
						)}

						{/* Navigation buttons */}
						{allImages.length > 1 && (
							<>
								<button
									onClick={handlePreviousImage}
									className="absolute top-1/2 left-4 h-8 w-8 -translate-y-1/2 rounded-full bg-black/50 text-white hover:bg-black/70"
								>
									<Icon name="chevron-left" className="size-6" />
								</button>
								<button
									onClick={handleNextImage}
									className="absolute top-1/2 right-4 h-8 w-8 -translate-y-1/2 rounded-full bg-black/50 text-white hover:bg-black/70"
								>
									<Icon name="chevron-right" className="size-6" />
								</button>
							</>
						)}

						{/* Image counter */}
						<div className="absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full bg-black/50 px-4 py-2 text-xs text-white">
							{currentImageIndex + 1} / {allImages.length}
						</div>
					</div>

					{/* Thumbnail strip */}
					{allImages.length > 1 && (
						<div className="bg-muted flex gap-2 overflow-x-auto p-4">
							{allImages.map((img, idx) => (
								<button
									key={img.id}
									onClick={() => setCurrentImageIndex(idx)}
									className={`flex-shrink-0 overflow-hidden rounded border-2 transition-all ${
										idx === currentImageIndex
											? 'border-primary'
											: 'border-transparent opacity-60 hover:opacity-100'
									}`}
								>
									<img
										src={img.url || ''}
										alt={img.altText || ''}
										className="h-16 w-20 object-cover"
									/>
								</button>
							))}
						</div>
					)}
				</DialogContent>
			</Dialog>

			<div className="bg-background min-h-screen pb-20">
				{/* Image Gallery Section */}
				<div className="mx-auto max-w-7xl p-4">
					{allImages.length > 0 ? (
						allImages.length === 1 ? (
							// Single image - full width
							<button
								onClick={() => handleImageClick(0)}
								className="h-[300px] w-full overflow-hidden rounded-xl md:h-[400px] lg:h-[500px]"
							>
								<img
									src={allImages[0]?.url || ''}
									alt={allImages[0]?.altText || rental.title}
									className="h-full w-full object-cover"
								/>
							</button>
						) : (
							// Multiple images - grid layout
							<div className="grid h-[300px] grid-cols-1 gap-2 overflow-hidden rounded-xl md:h-[400px] md:grid-cols-4 lg:h-[500px]">
								<button
									onClick={() => handleImageClick(0)}
									className="relative md:col-span-3 md:row-span-2"
								>
									<img
										src={allImages[0]?.url || ''}
										alt={allImages[0]?.altText || rental.title}
										className="h-full w-full object-cover"
									/>
								</button>
								<div className="hidden grid-rows-2 gap-2 md:grid">
									{allImages.slice(1, 3).map((img, idx) => (
										<button
											key={img.id}
											onClick={() => handleImageClick(idx + 1)}
											className="relative h-full overflow-hidden"
										>
											<img
												src={img.url || ''}
												alt={img.altText || ''}
												className="h-full w-full object-cover"
											/>
											{/* Show "See all photos" overlay on the last visible image if there are more */}
											{idx === 1 && allImages.length > 3 && (
												<div className="absolute inset-0 flex items-center justify-center bg-black/40 text-white transition-colors hover:bg-black/50">
													<span className="font-semibold">
														+{allImages.length - 3} more
													</span>
												</div>
											)}
										</button>
									))}
								</div>
							</div>
						)
					) : (
						// No images - placeholder
						<div className="bg-muted flex h-[300px] items-center justify-center rounded-xl md:h-[400px]">
							<Icon name="image" className="text-muted-foreground size-20" />
						</div>
					)}
				</div>

				<div className="mx-auto max-w-7xl px-4">
					<div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
						{/* Main Content Column */}
						<div className="lg:col-span-2">
							{/* Header Info */}
							<div className="mb-6">
								<div className="mb-2 flex flex-wrap gap-2">
									{rental.categories.map((c) => (
										<Badge key={c.id} variant="secondary">
											{c.name}
										</Badge>
									))}
								</div>
								<div className="flex items-center justify-between">
									<h1 className="mb-2 text-3xl font-bold tracking-tight sm:text-4xl">
										{rental.title}
									</h1>
									<div className="flex items-baseline justify-between">
										<span className="text-3xl font-bold">
											{primaryRate ? formatPrice(primaryRate) : 'Contact'}
										</span>
										{primaryRate && (
											<span className="text-muted-foreground">
												{primaryRateLabel}
											</span>
										)}
									</div>
								</div>
								<div className="text-muted-foreground flex items-center gap-1 text-sm">
									<Icon name="map-pin" />
									{rental.cities.map((c) => c.name).join(', ') ||
										'Location varies'}
								</div>
							</div>

							{/* Description */}
							<div className="mb-8">
								<p className="text-muted-foreground text-lg leading-relaxed whitespace-pre-line">
									{rental.description}
								</p>
							</div>

							{/* Rental Details Grid */}
							<div className="mb-8">
								<h2 className="bg-muted mb-4 p-2 text-xl font-bold">
									Rental Details
								</h2>
								<div className="grid grid-cols-1 gap-2 sm:grid-cols-2 sm:gap-4">
									{/* Item Details Card (Short Facts) */}
									{(rental.brand ||
										rental.model ||
										rental.year ||
										rental.condition ||
										rental.replacementValueCents) && (
										<div className="col-span-2 rounded-lg border p-4">
											<div className="mb-3 flex items-center gap-2 border-b pb-2 font-semibold">
												<Icon name="file-text" className="size-5" />
												<span> Details</span>
											</div>
											<dl className="space-y-2 text-sm">
												{rental.brand && (
													<div className="flex justify-between">
														<dt className="text-muted-foreground">Brand</dt>
														<dd className="font-medium">{rental.brand}</dd>
													</div>
												)}
												{rental.model && (
													<div className="flex justify-between">
														<dt className="text-muted-foreground">Model</dt>
														<dd className="font-medium">{rental.model}</dd>
													</div>
												)}
												{typeof rental.year === 'number' && (
													<div className="flex justify-between">
														<dt className="text-muted-foreground">Year</dt>
														<dd className="font-medium">{rental.year}</dd>
													</div>
												)}
												{rental.condition && (
													<div className="flex justify-between">
														<dt className="text-muted-foreground">Condition</dt>
														<dd className="font-medium">
															{CONDITION_LABELS[
																rental.condition as keyof typeof CONDITION_LABELS
															] || rental.condition}
														</dd>
													</div>
												)}
												{rental.replacementValueCents && (
													<div className="flex justify-between">
														<dt className="text-muted-foreground">
															Replacement Value
														</dt>
														<dd className="font-medium">
															{formatPrice(rental.replacementValueCents)}
														</dd>
													</div>
												)}
											</dl>
										</div>
									)}

									{/* Pricing Card */}
									{pricingOptions.length > 0 && (
										<div className="col-span-2 rounded-lg border p-4 sm:col-span-1">
											<div className="mb-3 flex items-center gap-2 border-b pb-2 font-semibold">
												<Icon name="badge-dollar-sign" className="size-5" />
												<span>Rates </span>
											</div>
											<dl className="space-y-2 text-sm">
												{pricingOptions.map((opt, idx) => (
													<div key={idx} className="flex justify-between">
														<dt className="text-muted-foreground">
															{opt.label}
														</dt>
														<dd className="font-medium">{opt.value}</dd>
													</div>
												))}
												{rental.depositCents && (
													<div className="flex justify-between border-t pt-2">
														<dt className="text-muted-foreground">Deposit</dt>
														<dd className="font-medium">
															{formatPrice(rental.depositCents)}
														</dd>
													</div>
												)}
											</dl>
										</div>
									)}

									{/* Availability & Terms Card */}
									<div className="col-span-2 rounded-lg border p-4 sm:col-span-1">
										<div className="mb-3 flex items-center gap-2 border-b pb-2 font-semibold">
											<Icon name="calendar" className="size-5" />
											<span>Terms</span>
										</div>
										<dl className="space-y-2 text-sm">
											{rental.minRentalAmount && (
												<div className="flex justify-between">
													<dt className="text-muted-foreground">Min Rental</dt>
													<dd className="font-medium">
														{rental.minRentalAmount}{' '}
														{rental.minRentalUnit || ''}
													</dd>
												</div>
											)}
											{rental.maxRentalAmount && (
												<div className="flex justify-between">
													<dt className="text-muted-foreground">Max Rental</dt>
													<dd className="font-medium">
														{rental.maxRentalAmount}{' '}
														{rental.maxRentalUnit || ''}
													</dd>
												</div>
											)}
											{typeof rental.leadTimeHours === 'number' && (
												<div className="flex justify-between">
													<dt className="text-muted-foreground">Lead Time</dt>
													<dd className="font-medium">
														{rental.leadTimeHours} hours
													</dd>
												</div>
											)}
											{rental.quantityAvailable != null && (
												<div className="flex justify-between">
													<dt className="text-muted-foreground">Available</dt>
													<dd className="font-medium">
														{rental.quantityAvailable}
													</dd>
												</div>
											)}
											{(rental.availableFrom || rental.availableUntil) && (
												<div className="border-t pt-2">
													<dt className="text-muted-foreground mb-1">
														Availability Dates
													</dt>
													<dd className="text-xs font-medium">
														{availFromStr} - {availUntilStr}
													</dd>
												</div>
											)}
										</dl>
									</div>

									{/* Additional Fees Card */}
									{(rental.cleaningFeeCents ||
										rental.setupFeeCents ||
										rental.deliveryFeeCents ||
										rental.lateReturnFeeCentsPerHour ||
										rental.operatorFeeCents) && (
										<div className="col-span-2 rounded-lg border p-4 sm:col-span-1">
											<div className="mb-3 flex items-center gap-2 border-b pb-2 font-semibold">
												<Icon name="badge-dollar-sign" className="size-5" />
												<span>Additional Fees</span>
											</div>
											<dl className="space-y-2 text-sm">
												{rental.cleaningFeeCents && (
													<div className="flex justify-between">
														<dt className="text-muted-foreground">
															Cleaning Fee
														</dt>
														<dd className="font-medium">
															{formatPrice(rental.cleaningFeeCents)}
														</dd>
													</div>
												)}
												{rental.setupFeeCents && (
													<div className="flex justify-between">
														<dt className="text-muted-foreground">Setup Fee</dt>
														<dd className="font-medium">
															{formatPrice(rental.setupFeeCents)}
														</dd>
													</div>
												)}
												{rental.deliveryFeeCents && (
													<div className="flex justify-between">
														<dt className="text-muted-foreground">
															Delivery Fee
														</dt>
														<dd className="font-medium">
															{formatPrice(rental.deliveryFeeCents)}
															{rental.deliveryFeeIsStartingAt
																? ' (starting at)'
																: ''}
														</dd>
													</div>
												)}
												{rental.operatorFeeCents && (
													<div className="flex justify-between">
														<dt className="text-muted-foreground">
															Operator Fee
														</dt>
														<dd className="font-medium">
															{formatPrice(rental.operatorFeeCents)}
														</dd>
													</div>
												)}
												{rental.lateReturnFeeCentsPerHour && (
													<div className="flex justify-between">
														<dt className="text-muted-foreground">
															Late Return
														</dt>
														<dd className="font-medium">
															{formatPrice(rental.lateReturnFeeCentsPerHour)} /
															hour
														</dd>
													</div>
												)}
											</dl>
										</div>
									)}

									{/* Services Card (Delivery, Setup, Operator Availability) */}
									{(typeof rental.deliveryAvailable === 'boolean' ||
										typeof rental.setupIncluded === 'boolean' ||
										typeof rental.operatorAvailable === 'boolean') && (
										<div className="col-span-2 rounded-lg border p-4 sm:col-span-1">
											<div className="mb-3 flex items-center gap-2 border-b pb-2 font-semibold">
												<Icon name="truck" className="size-5" />
												<span>Services</span>
											</div>
											<dl className="space-y-2 text-sm">
												{typeof rental.deliveryAvailable === 'boolean' && (
													<div className="flex justify-between">
														<dt className="text-muted-foreground">
															Delivery Available
														</dt>
														<dd className="font-medium">
															{rental.deliveryAvailable ? 'Yes' : 'No'}
															{rental.deliveryRadiusMiles &&
																` (within ${rental.deliveryRadiusMiles} miles)`}
														</dd>
													</div>
												)}
												{typeof rental.setupIncluded === 'boolean' && (
													<div className="flex justify-between">
														<dt className="text-muted-foreground">
															Setup Included
														</dt>
														<dd className="font-medium">
															{rental.setupIncluded ? 'Yes' : 'No'}
														</dd>
													</div>
												)}
												{typeof rental.operatorAvailable === 'boolean' && (
													<div className="flex justify-between">
														<dt className="text-muted-foreground">
															Operator Available
														</dt>
														<dd className="font-medium">
															{rental.operatorAvailable ? 'Yes' : 'No'}
														</dd>
													</div>
												)}
											</dl>
										</div>
									)}
								</div>
							</div>

							{/* Full-Width Sections for Long-Form Content */}

							{/* Specifications & Storage */}
							{(rental.specs || rental.storageRequirements) && (
								<div className="mb-8">
									<h2 className="bg-muted mb-4 p-2 text-xl font-bold">
										Specifications
									</h2>
									<div className="space-y-6 px-2">
										{rental.specs && (
											<div>
												<h3 className="font-semibold">Specs</h3>
												<p className="text-muted-foreground leading-relaxed whitespace-pre-line">
													{rental.specs}
												</p>
											</div>
										)}
										{rental.storageRequirements && (
											<div>
												<h3 className="font-semibold">Storage Requirements</h3>
												<p className="text-muted-foreground leading-relaxed whitespace-pre-line">
													{rental.storageRequirements}
												</p>
											</div>
										)}
									</div>
								</div>
							)}

							{/* Logistics & Instructions */}
							{(rental.pickupInstructions ||
								rental.setupDescription ||
								rental.operatorDescription) && (
								<div className="mb-8">
									<h2 className="bg-muted mb-4 p-2 text-xl font-bold">
										Logistics & Instructions
									</h2>
									<div className="space-y-6 px-2">
										{rental.pickupInstructions && (
											<div>
												<h3 className="font-semibold">Pickup Instructions</h3>
												<p className="text-muted-foreground leading-relaxed whitespace-pre-line">
													{rental.pickupInstructions}
												</p>
											</div>
										)}
										{rental.setupDescription && (
											<div>
												<h3 className="font-semibold">Setup Details</h3>
												<p className="text-muted-foreground leading-relaxed whitespace-pre-line">
													{rental.setupDescription}
												</p>
											</div>
										)}
										{rental.operatorDescription && (
											<div>
												<h3 className="font-semibold">Operator Details</h3>
												<p className="text-muted-foreground leading-relaxed whitespace-pre-line">
													{rental.operatorDescription}
												</p>
											</div>
										)}
									</div>
								</div>
							)}

							{/* Policies & Rules */}
							{(rental.damagePolicy ||
								rental.requirementsText ||
								(rental.allowedRules?.length ?? 0) > 0 ||
								(rental.notAllowedRules?.length ?? 0) > 0) && (
								<div className="mb-8">
									<h2 className="bg-muted mb-4 p-2 text-xl font-bold">
										Policies & Rental Rules
									</h2>
									<div className="grid gap-6 px-2 md:grid-cols-2">
										{rental.damagePolicy && (
											<div className="md:col-span-2">
												<h3 className="font-semibold">Damage Policy</h3>
												<p className="text-muted-foreground leading-relaxed whitespace-pre-line">
													{rental.damagePolicy}
												</p>
											</div>
										)}
										{rental.requirementsText && (
											<div className="md:col-span-2">
												<h3 className="font-semibold">Requirements</h3>
												<p className="text-muted-foreground leading-relaxed whitespace-pre-line">
													{rental.requirementsText}
												</p>
											</div>
										)}

										{(rental.allowedRules?.length ?? 0) > 0 && (
											<div>
												<h3 className="text-success flex items-center gap-2 font-semibold">
													<Icon name="check-circled" className="size-4" />
													Allowed
												</h3>
												<ul className="text-muted-foreground space-y-1">
													{rental.allowedRules!.map((rule, i) => (
														<li key={i} className="flex items-start gap-2">
															<span className="mt-2 size-1 rounded-full bg-current" />
															<span>{rule}</span>
														</li>
													))}
												</ul>
											</div>
										)}

										{(rental.notAllowedRules?.length ?? 0) > 0 && (
											<div>
												<h3 className="text-destructive flex items-center gap-2 font-semibold">
													<Icon name="cross-circled" className="size-4" />
													Not Allowed
												</h3>
												<ul className="text-muted-foreground space-y-1">
													{rental.notAllowedRules!.map((rule, i) => (
														<li key={i} className="flex items-start gap-2">
															<span className="mt-2 size-1 rounded-full bg-current" />
															<span>{rule}</span>
														</li>
													))}
												</ul>
											</div>
										)}
									</div>
								</div>
							)}

							{/* Additional Images Section */}
							{allImages.length > 3 && (
								<>
									<div className="mb-8">
										<h2 className="bg-muted mb-4 p-2 text-xl font-bold">
											Photos
										</h2>
										<div className="grid grid-cols-2 gap-4 md:grid-cols-3">
											{allImages.map((image, idx) => (
												<button
													key={idx}
													onClick={() => handleImageClick(idx)}
													className="relative overflow-clip rounded-md border transition-transform hover:scale-105"
												>
													<img
														src={image.url || ''}
														alt={image.altText || rental.title}
														loading="lazy"
														decoding="async"
														className="aspect-square h-auto w-full object-cover object-center"
													/>
												</button>
											))}
										</div>
									</div>
								</>
							)}

							{/* Cancellation Policy Section */}
							{rental.cancellationPolicy && (
								<div className="mb-8">
									<h2 className="bg-muted mb-4 p-2 text-xl font-bold">
										Cancellation Policy
									</h2>
									<div className="space-y-4 px-2">
										{rental.cancellationPolicy === 'flexible' && (
											<div className="space-y-3">
												<p className="text-muted-foreground leading-relaxed">
													<strong className="text-foreground">
														Renters can cancel up to 24 hours before
														pickup/delivery for a full refund.
													</strong>
												</p>
												<ul className="text-muted-foreground space-y-2">
													<li className="flex items-start gap-2">
														<Icon
															name="check-circled"
															className="text-success mt-1 size-4 shrink-0"
														/>
														<span>
															Cancel up to 24 hours before pickup/delivery for a
															full refund
														</span>
													</li>
													<li className="flex items-start gap-2">
														<Icon
															name="clock"
															className="text-warning mt-1 size-4 shrink-0"
														/>
														<span>
															Cancellations within 24 hours may result in
															partial or no refund, at the owner's discretion
														</span>
													</li>
												</ul>
											</div>
										)}

										{rental.cancellationPolicy === 'moderate' && (
											<div className="space-y-3">
												<p className="text-muted-foreground leading-relaxed">
													<strong className="text-foreground">
														Renters can cancel up to 3 days before
														pickup/delivery for a full refund.
													</strong>
												</p>
												<ul className="text-muted-foreground space-y-2">
													<li className="flex items-start gap-2">
														<Icon
															name="check-circled"
															className="text-success mt-1 size-4 shrink-0"
														/>
														<span>
															Cancel up to 3 days before pickup/delivery for a
															full refund
														</span>
													</li>
													<li className="flex items-start gap-2">
														<Icon
															name="clock"
															className="text-warning mt-1 size-4 shrink-0"
														/>
														<span>
															Cancellations inside 3 days may result in partial
															or no refund, depending on the owner
														</span>
													</li>
												</ul>
											</div>
										)}

										{rental.cancellationPolicy === 'strict' && (
											<div className="space-y-3">
												<p className="text-muted-foreground leading-relaxed">
													<strong className="text-foreground">
														Renters must cancel at least 7 days before
														pickup/delivery to receive a full refund.
													</strong>
												</p>
												<ul className="text-muted-foreground space-y-2">
													<li className="flex items-start gap-2">
														<Icon
															name="check-circled"
															className="text-success mt-1 size-4 shrink-0"
														/>
														<span>
															Cancel at least 7 days before pickup/delivery for
															a full refund
														</span>
													</li>
													<li className="flex items-start gap-2">
														<Icon
															name="clock"
															className="text-warning mt-1 size-4 shrink-0"
														/>
														<span>
															Cancellations within 7 days may not qualify for
															any refund unless the owner chooses otherwise
														</span>
													</li>
												</ul>
											</div>
										)}

										<Alert className="mt-4">
											<Icon name="info-circled" className="size-4" />
											<AlertTitle>Important</AlertTitle>
											<AlertDescription>
												Cancellation and refunds are handled directly between
												the renter and the owner. RentDFW does not process
												payments or enforce cancellation policies.
											</AlertDescription>
										</Alert>
									</div>
								</div>
							)}

							{/* Rental Inquiry Contact Form */}
							<div className="mb-8">
								<h2 className="bg-muted mb-4 p-2 text-xl font-bold">
									Request to Book
								</h2>
								<div className="px-2">
									<p className="text-muted-foreground mb-6 text-sm">
										Fill out the form below to send an inquiry to the rental
										owner. They will respond directly to your contact
										information.
									</p>
									<Form method="post" {...getFormProps(pageForm)}>
										<HoneypotInputs />
										<input type="hidden" name="intent" value="submit-inquiry" />
										<input type="hidden" name="listingId" value={rental.id} />

										<InquiryFormFields fields={pageFields} rental={rental} />

										<Button
											type="submit"
											className="w-full"
											disabled={isDialogSubmitting}
											size="lg"
										>
											{isDialogSubmitting ? 'Sending...' : 'Send Inquiry'}
										</Button>
									</Form>
								</div>
							</div>

							{/* Browse Similar Rentals */}
							{rental.cities.length > 0 && rental.categories.length > 0 && (
								<div className="mb-8">
									<h2 className="bg-muted mb-4 p-2 text-xl font-bold">
										Nearby Rentals
									</h2>
									<ul role="list" className="flex flex-wrap gap-8 px-2">
										{[...rental.categories, ...rental.subcategories]
											.slice(0, 3)
											.map((category) =>
												rental.cities.slice(0, 2).map((city) => (
													<li
														key={`${category.id}-${city.id}`}
														className="group relative"
													>
														<Link
															to={`/rentals/${city.slug}/${category.slug}`}
															className="flex items-center justify-between gap-x-6 py-4"
														>
															<div className="min-w-0 flex-auto">
																<p className="text-foreground text-sm font-semibold group-hover:underline">
																	{city.name} {category.name}
																</p>
															</div>
														</Link>
													</li>
												)),
											)}
									</ul>
								</div>
							)}

							{/* Tags Section */}
							<div className="mb-8">
								<h2 className="bg-muted mb-4 p-2 text-xl font-bold">
									Rental Categories & Locations
								</h2>
								<div className="space-y-4 px-2">
									{rental.categories.length > 0 && (
										<div>
											<h3 className="text-muted-foreground mb-2 text-sm font-semibold">
												Categories
											</h3>
											<div className="flex flex-wrap gap-2">
												{rental.categories.map((category) => (
													<Link
														key={category.id}
														to={`/rentals/category/${category.slug}`}
													>
														<Badge
															variant="outline"
															className="hover:bg-accent"
														>
															{category.name}
														</Badge>
													</Link>
												))}
												{rental.subcategories.map((subcategory) => (
													<Link
														key={subcategory.id}
														to={`/rentals/category/${subcategory.slug}`}
													>
														<Badge
															variant="outline"
															className="hover:bg-accent"
														>
															{subcategory.name}
														</Badge>
													</Link>
												))}
											</div>
										</div>
									)}

									{rental.cities.length > 0 && (
										<div>
											<h3 className="text-muted-foreground mb-2 text-sm font-semibold">
												Available in
											</h3>
											<div className="flex flex-wrap gap-2">
												{rental.cities.map((city) => (
													<Link key={city.id} to={`/rentals/city/${city.slug}`}>
														<Badge
															variant="outline"
															className="hover:bg-accent"
														>
															<Icon name="map-pin" className="mr-1 size-3" />
															{city.name}
														</Badge>
													</Link>
												))}
											</div>
										</div>
									)}
								</div>
							</div>

							<div className="mb-6 flex items-center justify-end gap-2">
								<CopyButton textToCopy={canonicalUrl || ''} />

								<Dialog open={flagDialogOpen} onOpenChange={setFlagDialogOpen}>
									<Tooltip>
										<TooltipTrigger asChild>
											<DialogTrigger asChild>
												<Button variant="outline" size="icon">
													<Icon name="flag" />
												</Button>
											</DialogTrigger>
										</TooltipTrigger>
										<TooltipContent>
											<p>Report listing</p>
										</TooltipContent>
									</Tooltip>
									<DialogContent>
										<DialogHeader>
											<DialogTitle>Report an Issue</DialogTitle>
											<DialogDescription>
												Help us improve by reporting issues with this rental.
											</DialogDescription>
										</DialogHeader>
										<FeedbackForm
											variant="flag"
											defaultValues={{ pageUrl: canonicalUrl }}
											onSuccess={() => setFlagDialogOpen(false)}
										/>
									</DialogContent>
								</Dialog>
							</div>
						</div>

						{/* Sidebar Column */}
						<div className="space-y-6 lg:col-span-1">
							<div className="sticky top-24 space-y-4">
								<Card className="shadow-lg">
									<CardContent className="space-y-4">
										<Dialog
											open={bookingDialogOpen}
											onOpenChange={setBookingDialogOpen}
										>
											<DialogTrigger asChild>
												<Button className="w-full" size="lg">
													<Icon name="calendar" />
													Book Rental Now
												</Button>
											</DialogTrigger>
											<DialogContent className="max-w-lg">
												<DialogHeader>
													<DialogTitle>Request Booking</DialogTitle>
													<DialogDescription>
														Send an inquiry to the owner about renting this
														item.
													</DialogDescription>
												</DialogHeader>
												<inquiryFetcher.Form
													method="post"
													{...getFormProps(dialogForm)}
												>
													<HoneypotInputs />
													<input
														type="hidden"
														name="intent"
														value="submit-inquiry"
													/>
													<input
														type="hidden"
														name="listingId"
														value={rental.id}
													/>

													<InquiryFormFields
														fields={dialogFields}
														rental={rental}
													/>

													<Button
														type="submit"
														className="w-full"
														disabled={isDialogSubmitting}
													>
														{isDialogSubmitting ? 'Sending...' : 'Send Inquiry'}
													</Button>
												</inquiryFetcher.Form>
											</DialogContent>
										</Dialog>

										{/* Owner/Business Card */}
										{(rental.ownerBusiness || rental.ownerUser) && (
											<>
												<Separator className="mt-2 mb-4" />

												<div className="flex items-center gap-3">
													<div className="bg-muted flex size-12 items-center justify-center rounded-full">
														<Icon
															name={
																rental.ownerBusiness ? 'building-2' : 'avatar'
															}
															className="size-6 opacity-60"
														/>
													</div>
													<div>
														{rental.ownerBusiness ? (
															<>
																<Link
																	to={`/businesses/${rental.ownerBusiness.slug}`}
																	className="hover:underline"
																>
																	<span className="block leading-none font-bold">
																		{rental.ownerBusiness.name}
																	</span>
																</Link>
																<span className="text-muted-foreground text-xs">
																	Business
																</span>
															</>
														) : rental.ownerUser ? (
															<div>
																<span className="block leading-none font-bold">
																	{rental.ownerUser.name}
																</span>
																<span className="text-muted-foreground text-xs">
																	Private Owner
																</span>
															</div>
														) : null}
													</div>
												</div>
											</>
										)}
									</CardContent>
								</Card>
							</div>
						</div>
					</div>
				</div>
			</div>
		</>
	)
}
